import { useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle, Phone, ShieldCheck } from 'lucide-react';

import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { usePatientSession } from './patientSession';

export function LoginDialog({
  pharmacySlug,
  open,
  onClose,
  onSuccess,
}: {
  pharmacySlug: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { sendOtp, verifyOtp } = usePatientSession();

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStep('phone');
    setPhone('');
    setCode('');
    setName('');
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  async function handleSendOtp(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await sendOtp(pharmacySlug, phone.trim());
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the code. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyOtp(pharmacySlug, phone.trim(), code.trim(), name.trim() || undefined);
      reset();
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not verify that code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{step === 'phone' ? 'Log in to order' : 'Enter the code'}</DialogTitle>
          <DialogDescription>
            {step === 'phone'
              ? "We'll text a one-time code to verify it's you."
              : `Sent to ${phone}. Valid for 10 minutes.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'phone' ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="patient-phone">Phone number</Label>
              <div className="relative">
                <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="patient-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-9"
                  placeholder="98765 43210"
                />
              </div>
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : 'Send code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="patient-code">Verification code</Label>
              <div className="relative">
                <ShieldCheck className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="patient-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="pl-9"
                  placeholder="123456"
                  autoFocus
                />
              </div>
            </div>
            {/* Only matters the first time this phone logs in at this pharmacy — a returning
                patient's saved name is never overwritten (see PatientAuthController.VerifyOtp). */}
            <div className="space-y-1.5">
              <Label htmlFor="patient-name">Your name (first time only)</Label>
              <Input id="patient-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : 'Verify & continue'}
            </Button>
            <button
              type="button"
              onClick={() => setStep('phone')}
              className="w-full text-center text-xs text-muted-foreground hover:underline"
            >
              Use a different number
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
